import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Search, CreditCard, Receipt, Calendar, User, ArrowRight } from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };
const MB = { card: "bg-[#205B5A]/10 text-[#205B5A]", cash: "bg-amber-50 text-amber-600", "bank-transfer": "bg-blue-50 text-blue-600", other: "bg-[#1A1B1A]/5 text-[#757675]" };

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = search ? `?search=${search}` : "";
    try {
      const [pRes, sRes] = await Promise.all([api.get(`/payments${params}`), api.get("/payments/summary")]);
      setPayments(pRes.data.payments); setTotal(pRes.data.total); setSummary(sRes.data);
    } catch {}
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="payments-page">
      <div className="mb-6"><h1 className="text-2xl font-medium text-[#1A1B1A]" style={H}>Payments</h1><p className="text-sm text-[#757675]">{total} payment records</p></div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="payment-summary">
        {[
          { label: "Today", value: `£${(summary.today_total || 0).toFixed(2)}`, color: "#205B5A" },
          { label: "This week", value: `£${(summary.week_total || 0).toFixed(2)}`, color: "#496275" },
          { label: "This month", value: `£${(summary.month_total || 0).toFixed(2)}`, color: "#205B5A" },
          { label: "Outstanding", value: `£${(summary.outstanding_amount || 0).toFixed(2)}`, color: summary.outstanding_amount > 0 ? "#C4553A" : "#205B5A" },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-4">
            <p className="text-[10px] text-[#757675] uppercase tracking-wider mb-1">{s.label}</p>
            <p className="text-xl font-medium" style={{ ...H, color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="relative mb-5"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#757675]" /><input value={search} onChange={e => setSearch(e.target.value)} data-testid="payment-search" className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 bg-white text-sm focus:outline-none focus:border-[#205B5A]" placeholder="Search by reference, patient, invoice..." /></div>

      <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
        <table className="w-full"><thead><tr className="border-b border-[#1A1B1A]/[0.06]">
          <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase">Reference</th>
          <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase">Patient</th>
          <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase hidden md:table-cell">Date</th>
          <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase hidden md:table-cell">Method</th>
          <th className="text-right px-5 py-3 text-[10px] font-medium text-[#757675] uppercase">Amount</th>
          <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase hidden lg:table-cell">Invoice</th>
        </tr></thead><tbody>
          {payments.map(p => (
            <tr key={p.id} className="border-b border-[#1A1B1A]/[0.03] hover:bg-[#FAF9F6] cursor-pointer" onClick={() => setDetail(p)}>
              <td className="px-5 py-3 text-sm font-medium text-[#1A1B1A] font-mono">{p.reference}</td>
              <td className="px-5 py-3 text-sm text-[#4A4B4A]">{p.patient_name}</td>
              <td className="px-5 py-3 text-sm text-[#757675] hidden md:table-cell">{p.date} {p.time}</td>
              <td className="px-5 py-3 hidden md:table-cell"><span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full capitalize ${MB[p.payment_method] || MB.other}`}>{p.payment_method}</span></td>
              <td className="px-5 py-3 text-sm font-semibold text-[#205B5A] text-right">£{p.amount?.toFixed(2)}</td>
              <td className="px-5 py-3 text-sm text-[#757675] hidden lg:table-cell">{p.invoice_number || "—"}</td>
            </tr>
          ))}
          {payments.length === 0 && !loading && <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-[#757675]">No payments recorded yet. Payments are created automatically when invoices are marked paid.</td></tr>}
        </tbody></table>
      </div>

      {/* Payment detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="payment-detail-drawer"><div className="absolute inset-0 bg-black/20" onClick={() => setDetail(null)} />
          <div className="relative w-full max-w-md bg-white shadow-2xl overflow-y-auto"><div className="p-5">
            <div className="flex items-center justify-between mb-5"><h2 className="text-lg font-medium text-[#1A1B1A]" style={H}>{detail.reference}</h2><button onClick={() => setDetail(null)} className="text-[#757675]">×</button></div>
            <div className="bg-[#205B5A]/[0.03] rounded-xl p-4 mb-4 text-center"><p className="text-2xl font-semibold text-[#205B5A]" style={H}>£{detail.amount?.toFixed(2)}</p><p className="text-xs text-[#757675]">Payment received</p></div>
            <div className="space-y-2.5">
              <div className="flex items-center gap-2 text-xs"><User className="w-3 h-3 text-[#757675]" /><span className="text-[#757675]">Patient</span><Link to={`/app/patients/${detail.patient_id}`} className="ml-auto font-medium text-[#205B5A]">{detail.patient_name}</Link></div>
              <div className="flex items-center gap-2 text-xs"><Calendar className="w-3 h-3 text-[#757675]" /><span className="text-[#757675]">Date & time</span><span className="ml-auto font-medium text-[#1A1B1A]">{detail.date} at {detail.time}</span></div>
              <div className="flex items-center gap-2 text-xs"><CreditCard className="w-3 h-3 text-[#757675]" /><span className="text-[#757675]">Method</span><span className="ml-auto font-medium text-[#1A1B1A] capitalize">{detail.payment_method}</span></div>
              {detail.invoice_number && <div className="flex items-center gap-2 text-xs"><Receipt className="w-3 h-3 text-[#757675]" /><span className="text-[#757675]">Invoice</span><Link to={`/app/invoices/${detail.invoice_id}`} className="ml-auto font-medium text-[#205B5A]">{detail.invoice_number} <ArrowRight className="w-3 h-3 inline" /></Link></div>}
              {detail.practitioner_name && <div className="flex items-center gap-2 text-xs"><span className="text-[#757675]">Practitioner</span><span className="ml-auto font-medium text-[#1A1B1A]">{detail.practitioner_name}</span></div>}
              <div className="flex items-center gap-2 text-xs"><span className="text-[#757675]">Recorded by</span><span className="ml-auto font-medium text-[#1A1B1A]">{detail.recorded_by_name}</span></div>
            </div>
          </div></div>
        </div>
      )}
    </div>
  );
}
